import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Eye, EyeOff, MapPin, Search, UserPlus } from 'lucide-react'
import { Modal } from '../ui/Modal'
import { Spinner } from '../ui/Spinner'
import { EmptyState } from '../ui/EmptyState'
import { StaffAvatar } from '../payroll/staff/StaffAvatar'
import { supabase } from '../../lib/supabase'
import { fetchWarehouses } from '../../lib/supabaseQueries'
import toast from 'react-hot-toast'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)

interface RoleOption {
  id: string
  name: string
  description: string | null
}

interface AddUserFromStaffModalProps {
  open: boolean
  onClose: () => void
  tenantId: string
  roles: RoleOption[]
}

interface StaffPick {
  id: string
  personnel_number: number
  full_name: string
  first_name: string
  last_name: string
  email: string | null
  phone: string | null
  job_title: string | null
}

type StaffRowRaw = {
  id: string
  personnel_number: number
  full_name: string
  first_name: string
  last_name: string
  email: string | null
  phone: string | null
  job_title_other: string | null
  job_titles: { title: string } | null
}

const LEGACY_ROLES = ['owner', 'admin', 'location_admin', 'manager', 'cashier', 'accountant', 'viewer'] as const
type LegacyRole = (typeof LEGACY_ROLES)[number]

function toLegacyRole(roleName: string): LegacyRole {
  const lower = roleName.toLowerCase().replace(/\s+/g, '_')
  return LEGACY_ROLES.includes(lower as LegacyRole) ? (lower as LegacyRole) : 'viewer'
}

function sanitizeUsernameBase(first: string, last: string): string {
  const raw = `${first}.${last}`.toLowerCase().replace(/\s+/g, '.')
  const cleaned = raw.replace(/[^a-z0-9.-]/g, '').replace(/\.+/g, '.').replace(/^\.|\.$/g, '')
  return cleaned || 'user'
}

async function findAvailableUsername(tenantId: string, base: string): Promise<string> {
  // Pull all conflicting usernames in one query so we can pick the lowest free suffix.
  const { data } = await db('tenant_users')
    .select('username')
    .eq('tenant_id', tenantId)
    .like('username', `${base}%`)
  const used = new Set<string>(
    ((data ?? []) as { username: string | null }[])
      .map(r => (r.username ?? '').toLowerCase())
      .filter(u => u.length > 0),
  )
  if (!used.has(base)) return base
  let n = 2
  while (used.has(`${base}${n}`)) n++
  return `${base}${n}`
}

export function AddUserFromStaffModal({ open, onClose, tenantId, roles }: AddUserFromStaffModalProps) {
  const qc = useQueryClient()

  const [step, setStep] = useState<'pick' | 'confirm'>('pick')
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selected, setSelected] = useState<StaffPick | null>(null)

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [username, setUsername] = useState('')
  const [usernameStatus, setUsernameStatus] = useState<'idle' | 'checking' | 'available' | 'taken'>('idle')
  const [roleId, setRoleId] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [pwdShow, setPwdShow] = useState(false)
  const [confirmShow, setConfirmShow] = useState(false)
  // Location access
  const [accessMode, setAccessMode] = useState<'all' | 'specific'>('all')
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([])
  const [locationError, setLocationError] = useState('')

  // Reset when the modal closes.
  useEffect(() => {
    if (open) return
    setStep('pick')
    setSearch('')
    setDebouncedSearch('')
    setSelected(null)
    setFirstName(''); setLastName(''); setEmail(''); setPhone('')
    setUsername(''); setUsernameStatus('idle'); setRoleId('')
    setPassword(''); setConfirmPassword('')
    setPwdShow(false); setConfirmShow(false)
    setAccessMode('all'); setSelectedLocationIds([]); setLocationError('')
  }, [open])

  const selectedRole = roles.find(r => r.id === roleId)
  const legacyRole: LegacyRole = selectedRole ? toLegacyRole(selectedRole.name) : 'viewer'
  const isPrivileged = legacyRole === 'owner' || legacyRole === 'admin'
  const requiresLocation = legacyRole === 'location_admin'

  useEffect(() => {
    if (requiresLocation && accessMode !== 'specific') {
      setAccessMode('specific')
    }
  }, [requiresLocation, accessMode])

  const { data: warehouses = [] } = useQuery({
    queryKey: ['warehouses', tenantId],
    enabled: open && !!tenantId,
    queryFn: () => fetchWarehouses(tenantId),
  })

  // 200ms debounce on the search input.
  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 200)
    return () => clearTimeout(h)
  }, [search])

  // Active staff in this tenant.
  const { data: staff = [], isLoading: loadingStaff } = useQuery<StaffPick[]>({
    queryKey: ['staff-for-user-picker', tenantId],
    enabled: open && !!tenantId,
    queryFn: async () => {
      const { data, error } = await db('staff')
        .select(`
          id, personnel_number, full_name, first_name, last_name,
          email, phone, job_title_other,
          job_titles ( title )
        `)
        .eq('tenant_id', tenantId)
        .eq('employment_status', 'active')
        .order('full_name', { ascending: true })
      if (error) throw error
      return ((data ?? []) as StaffRowRaw[]).map(r => ({
        id: r.id,
        personnel_number: r.personnel_number,
        full_name: r.full_name,
        first_name: r.first_name,
        last_name: r.last_name,
        email: r.email,
        phone: r.phone,
        job_title: r.job_titles?.title ?? r.job_title_other ?? null,
      }))
    },
  })

  // Staff already linked to a user — exclude from picker.
  const { data: linkedStaffIds = [], isLoading: loadingLinked } = useQuery<string[]>({
    queryKey: ['linked-staff-ids', tenantId],
    enabled: open && !!tenantId,
    queryFn: async () => {
      const { data, error } = await db('tenant_users')
        .select('staff_id')
        .eq('tenant_id', tenantId)
        .not('staff_id', 'is', null)
      if (error) throw error
      return ((data ?? []) as { staff_id: string | null }[])
        .map(r => r.staff_id)
        .filter((id): id is string => !!id)
    },
  })

  const availableStaff = useMemo(() => {
    const linked = new Set(linkedStaffIds)
    return staff.filter(s => !linked.has(s.id))
  }, [staff, linkedStaffIds])

  const filteredStaff = useMemo(() => {
    if (!debouncedSearch) return availableStaff
    return availableStaff.filter(s =>
      s.full_name.toLowerCase().includes(debouncedSearch) ||
      String(s.personnel_number).includes(debouncedSearch),
    )
  }, [availableStaff, debouncedSearch])

  async function handlePick(staffRow: StaffPick) {
    setSelected(staffRow)
    setFirstName(staffRow.first_name)
    setLastName(staffRow.last_name)
    setEmail(staffRow.email ?? '')
    setPhone(staffRow.phone ?? '')

    const base = sanitizeUsernameBase(staffRow.first_name, staffRow.last_name)
    setUsernameStatus('checking')
    const free = await findAvailableUsername(tenantId, base)
    setUsername(free)
    setUsernameStatus('available')
    setStep('confirm')
  }

  async function checkUsernameUnique(value: string) {
    const v = value.trim().toLowerCase()
    if (!v) { setUsernameStatus('idle'); return }
    setUsernameStatus('checking')
    const { data } = await db('tenant_users')
      .select('id')
      .eq('tenant_id', tenantId)
      .eq('username', v)
      .maybeSingle()
    setUsernameStatus(data ? 'taken' : 'available')
  }

  const createUser = useMutation({
    mutationFn: async () => {
      if (!selected) throw new Error('No staff selected')
      const fn = firstName.trim()
      const ln = lastName.trim()
      const em = email.trim().toLowerCase()
      const un = username.trim().toLowerCase()
      const ph = phone.trim()

      if (!fn || !ln) throw new Error('First and last name are required')
      if (!em || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) throw new Error('Enter a valid email address')
      if (!un) throw new Error('Username is required')
      if (!/^[a-z0-9.-]+$/.test(un)) throw new Error('Username may only contain lowercase letters, numbers, dots and hyphens')
      if (!roleId) throw new Error('Please select a role')
      if (password.length < 8) throw new Error('Password must be at least 8 characters')
      if (password !== confirmPassword) throw new Error('Passwords do not match')

      // Email uniqueness in this tenant
      const { data: emailTaken } = await db('tenant_users')
        .select('id')
        .eq('tenant_id', tenantId)
        .eq('email', em)
        .maybeSingle()
      if (emailTaken) throw new Error('This email is already in your team. Use a different email address.')

      // Username uniqueness in this tenant
      const { data: usernameTaken } = await db('tenant_users')
        .select('id')
        .eq('tenant_id', tenantId)
        .eq('username', un)
        .maybeSingle()
      if (usernameTaken) throw new Error('That username is taken. Pick a different one.')

      const localSelectedRole = roles.find(r => r.id === roleId)
      const localLegacyRole: LegacyRole = localSelectedRole ? toLegacyRole(localSelectedRole.name) : 'viewer'
      const localIsPrivileged = localLegacyRole === 'owner' || localLegacyRole === 'admin'
      const localRequiresLocation = localLegacyRole === 'location_admin'

      if (localRequiresLocation && selectedLocationIds.length === 0) {
        setLocationError('Location Admin role requires at least one location.')
        throw new Error('Location Admin role requires at least one location.')
      }
      if (!localIsPrivileged && !localRequiresLocation && accessMode === 'specific' && selectedLocationIds.length === 0) {
        setLocationError('Select at least one location or choose All.')
        throw new Error('Select at least one location or choose All.')
      }
      setLocationError('')

      const payloadLocationIds = localIsPrivileged
        ? []
        : (accessMode === 'specific' ? selectedLocationIds : [])

      const fullName = `${fn} ${ln}`.trim()

      const { data, error } = await supabase.functions.invoke('create-user', {
        body: {
          email: em,
          password,
          full_name: fullName,
          first_name: fn,
          last_name: ln,
          phone: ph || null,
          username: un,
          role: localLegacyRole,
          role_id: roleId,
          staff_id: selected.id,
          location_ids: payloadLocationIds,
        },
      })
      if (error) {
        const msg = (data as { error?: string } | null)?.error ?? error.message
        throw new Error(msg || 'Failed to create user')
      }
      if (data && typeof data === 'object' && 'error' in data && data.error) {
        throw new Error(String((data as { error: string }).error))
      }

      return { fullName }
    },
    onSuccess: ({ fullName }) => {
      qc.invalidateQueries({ queryKey: ['tenant-users'] })
      qc.invalidateQueries({ queryKey: ['linked-staff-ids', tenantId] })
      qc.invalidateQueries({ queryKey: ['user-location-assignments'] })
      toast.success(`User created. ${fullName} can now log in.`)
      onClose()
    },
    onError: (e: Error) => toast.error(e.message),
  })

  const usernameHint = (() => {
    if (usernameStatus === 'checking') return 'Checking availability…'
    if (usernameStatus === 'taken') return 'That username is taken. Pick a different one.'
    return 'Auto-generated. Can be edited.'
  })()
  const usernameHintColor =
    usernameStatus === 'taken' ? 'text-red-500'
      : usernameStatus === 'available' ? 'text-sage-600'
        : 'text-gray-500'

  const isLoadingList = loadingStaff || loadingLinked

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={step === 'pick' ? 'Add User from Staff' : 'Confirm User Details'}
      size="xl"
    >
      {step === 'pick' ? (
        <div className="flex flex-col gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              autoFocus
              className="input pl-9"
              placeholder="Search staff by name or personnel number"
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <p className="text-xs text-gray-500">Showing staff who don&apos;t yet have user access.</p>

          {isLoadingList ? (
            <div className="flex justify-center py-10"><Spinner size="lg" /></div>
          ) : availableStaff.length === 0 ? (
            <EmptyState
              title="All set"
              description="All active staff already have user access."
              icon={<UserPlus className="h-8 w-8" />}
            />
          ) : filteredStaff.length === 0 ? (
            <p className="py-10 text-center text-sm text-gray-500">No staff match &ldquo;{search}&rdquo;.</p>
          ) : (
            <div className="max-h-[50vh] overflow-y-auto -mx-2 px-2 divide-y divide-gray-100 rounded-xl border border-gray-100">
              {filteredStaff.map(s => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => handlePick(s)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 cursor-pointer text-left transition-colors"
                >
                  <StaffAvatar firstName={s.first_name} lastName={s.last_name} photoUrl={null} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{s.full_name}</p>
                    <p className="text-xs text-gray-500 truncate">
                      #{s.personnel_number}{s.job_title ? ` · ${s.job_title}` : ''}
                    </p>
                  </div>
                  <span className="text-xs font-semibold text-brand-600 flex-shrink-0">Select →</span>
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {selected && (
            <div className="flex items-center gap-3 rounded-xl bg-brand-50 px-3 py-2.5">
              <StaffAvatar firstName={selected.first_name} lastName={selected.last_name} photoUrl={null} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">{selected.full_name}</p>
                <p className="text-xs text-brand-700">From Staff #{selected.personnel_number}</p>
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">First Name *</label>
              <input className="input" value={firstName} onChange={e => setFirstName(e.target.value)} />
            </div>
            <div>
              <label className="label">Last Name *</label>
              <input className="input" value={lastName} onChange={e => setLastName(e.target.value)} />
            </div>
            <div>
              <label className="label">Email *</label>
              <input className="input" type="email" value={email} onChange={e => setEmail(e.target.value)} />
            </div>
            <div>
              <label className="label">Phone</label>
              <input className="input" value={phone} onChange={e => setPhone(e.target.value)} />
            </div>
            <div className="sm:col-span-2">
              <label className="label">Username *</label>
              <input
                className="input"
                value={username}
                onChange={e => {
                  const v = e.target.value.toLowerCase().replace(/[^a-z0-9.-]/g, '')
                  setUsername(v)
                  setUsernameStatus('idle')
                }}
                onBlur={e => void checkUsernameUnique(e.target.value)}
              />
              <p className={`mt-1 text-xs ${usernameHintColor}`}>{usernameHint}</p>
            </div>
            <div className="sm:col-span-2">
              <label className="label">Role *</label>
              <select className="input" value={roleId} onChange={e => setRoleId(e.target.value)}>
                <option value="">— Select a role —</option>
                {roles.map(r => (
                  <option key={r.id} value={r.id}>
                    {r.name}{r.description ? ` — ${r.description}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Password *</label>
              <div className="relative">
                <input
                  className="input pr-10"
                  type={pwdShow ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Min. 8 characters"
                />
                <button
                  type="button"
                  onClick={() => setPwdShow(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {pwdShow ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div>
              <label className="label">Confirm Password *</label>
              <div className="relative">
                <input
                  className="input pr-10"
                  type={confirmShow ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={e => setConfirmPassword(e.target.value)}
                  placeholder="Repeat password"
                />
                <button
                  type="button"
                  onClick={() => setConfirmShow(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {confirmShow ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
          </div>

          {/* Location Access section */}
          <div className="border-t border-gray-100 pt-4">
            <div className="flex items-center gap-2 mb-3">
              <MapPin className="h-4 w-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-800">Location Access</h3>
            </div>

            {isPrivileged ? (
              <p className="text-xs text-gray-500 bg-gray-50 rounded-lg px-3 py-2.5">
                Owners and Admins always have access to all locations.
              </p>
            ) : (
              <div className="space-y-3">
                <div className="space-y-1.5">
                  <label className={`flex items-center gap-2 text-sm ${requiresLocation ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700 cursor-pointer'}`}>
                    <input
                      type="radio"
                      name="staff-access-mode"
                      checked={accessMode === 'all'}
                      disabled={requiresLocation}
                      onChange={() => { setAccessMode('all'); setLocationError('') }}
                    />
                    <span>All locations</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                    <input
                      type="radio"
                      name="staff-access-mode"
                      checked={accessMode === 'specific'}
                      onChange={() => setAccessMode('specific')}
                    />
                    <span>Specific locations</span>
                  </label>
                </div>

                {accessMode === 'specific' && (
                  <div className="space-y-2 border border-gray-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                    {warehouses.filter(w => w.is_active !== false).length === 0 ? (
                      <p className="text-xs text-gray-500">No locations available.</p>
                    ) : (
                      warehouses.filter(w => w.is_active !== false).map(w => {
                        const checked = selectedLocationIds.includes(w.id)
                        return (
                          <label key={w.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={e => {
                                setLocationError('')
                                setSelectedLocationIds(prev =>
                                  e.target.checked ? [...prev, w.id] : prev.filter(id => id !== w.id)
                                )
                              }}
                            />
                            <span>{w.name}</span>
                          </label>
                        )
                      })
                    )}
                  </div>
                )}

                {locationError && (
                  <p className="text-xs text-red-600">{locationError}</p>
                )}

                <p className="text-xs text-gray-500 bg-blue-50 rounded-lg px-3 py-2">
                  {requiresLocation
                    ? 'Location Admin role requires at least one location.'
                    : 'At least one location must be selected if restricting access.'}
                </p>
              </div>
            )}
          </div>

          <div className="flex flex-col-reverse sm:flex-row gap-2 pt-2">
            <button
              type="button"
              className="btn-secondary flex-1"
              onClick={() => setStep('pick')}
              disabled={createUser.isPending}
            >
              <ArrowLeft className="h-4 w-4" /> Back to staff list
            </button>
            <button
              type="button"
              className="btn-primary flex-1"
              disabled={createUser.isPending || usernameStatus === 'taken'}
              onClick={() => createUser.mutate()}
            >
              {createUser.isPending ? <Spinner size="sm" /> : 'Create User'}
            </button>
          </div>
        </div>
      )}
    </Modal>
  )
}
